import React, {useState} from 'react';
import Autocomplete, {AutocompleteProps} from '@material-ui/lab/Autocomplete';
import CircularProgress from '@material-ui/core/CircularProgress';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {makeStyles, TextField} from '@material-ui/core';
import {SEARCH_DEBOUNCE_DELAY} from '@shared/constants/Mapping';
import {useDebouncedCallback} from 'use-debounce';
import {useTranslation} from 'react-i18next';
import {INPUT_BORDER_COLOR, INPUT_LABEL_COLOR} from 'src/style/variable';

type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;
type WithOptional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;
interface IAutocompleteInputProps<T>
  extends WithOptional<
    AutocompleteProps<T, undefined, undefined, undefined>,
    'options' | 'renderInput'
  > {
  fetchOptions?: (searchedText: string) => Promise<T[] | undefined>;
  label: string;
  error?: Record<string, any>;
}

const useStyles = makeStyles(() => ({
  autoCompleteInput: {
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      transform: 'none'
    },
    '& .MuiInputLabel-formControl': {
      position: 'static',
      color: INPUT_LABEL_COLOR,
      fontSize: '1.1rem',
      marginBottom: '0.825rem'
    },
    '& .MuiOutlinedInput-notchedOutline legend': {
      display: 'none'
    },
    '& .MuiAutocomplete-inputRoot': {
      flexWrap: 'nowrap',
      height: '2rem'
    },
    '& .MuiInputBase-input': {
      marginTop: '-0.5rem'
    },
    '& .MuiFormControl-root': {
      paddingBottom: 0,
      marginBottom: 0
    },
    '& .MuiFormHelperText-root': {
      marginLeft: 0
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: INPUT_BORDER_COLOR
    }
  }
}));

const AutocompleteInput = <T,>(
  props: IAutocompleteInputProps<T>
): JSX.Element => {
  const {
    fetchOptions,
    className,
    label,
    placeholder,
    error,
    options: optionsList,
    ...rest
  } = props;
  const [options, setOptions] = React.useState<T[]>(
    optionsList ? optionsList : []
  );
  const [enteredValue, setEnteredValue] = React.useState('');
  const [loading, setLoading] = useState(false);
  const {enqueueSnackbar} = useSnackbar();
  const classes = useStyles();

  const updateOptions = async () => {
    if (!fetchOptions) return;
    try {
      setLoading(true);
      const filteredOptions = await fetchOptions(enteredValue);
      if (filteredOptions) {
        setOptions(filteredOptions);
      }
    } catch (err: any) {
      enqueueSnackbar(err?.message, SnackBarConfig('e'));
    } finally {
      setLoading(false);
    }
  };

  const {t} = useTranslation();

  const debouncedFetch = useDebouncedCallback(
    () => updateOptions(),
    SEARCH_DEBOUNCE_DELAY
  );

  React.useEffect(() => {
    if (!fetchOptions) return;
    debouncedFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enteredValue, debouncedFetch]);

  React.useEffect(() => {
    if (!open) {
      setOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleInputChange = (value: any) => {
    setEnteredValue(value);
  };

  return (
    <Autocomplete
      options={options}
      loading={loading}
      className={`${classes.autoCompleteInput} ${className}`}
      fullWidth
      // filterOptions has to be added because of this issue: https://github.com/mui/material-ui/issues/20068#issuecomment-812846503
      filterOptions={x => x}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant="outlined"
          error={!!error}
          helperText={error?.message ? t(error.message) : ''}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            )
          }}
          InputLabelProps={{
            shrink: true
          }}
        />
      )}
      inputValue={enteredValue}
      onInputChange={(e, newValue, reason) => {
        handleInputChange(newValue);
      }}
      {...rest}
    />
  );
};

export default AutocompleteInput;
